"use client"

import { useState, useEffect, useRef } from "react"
import { Send, Flame } from "lucide-react"
import { formatPrice } from "@/lib/utils"
import { getAvatarUrl } from "@/lib/getAvatarUrl"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import type { AuctionChatItem } from "@/lib/auction-types"

interface AuctionLiveChatProps {
  listingId: string
  currentUserId?: string | null
  currency?: string | null
  chatItems: AuctionChatItem[]
  onMessageSent: (payload: { item: AuctionChatItem }) => void
  canWrite?: boolean
}

export function AuctionLiveChat({ listingId, currentUserId, currency = "USD", chatItems, onMessageSent, canWrite = false }: AuctionLiveChatProps) {
  const [input, setInput] = useState("")
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      })
    }
  }, [chatItems])

  const handleSend = () => {
    const text = input.trim()
    if (!text || sending || !currentUserId || !canWrite) return
    setSending(true)
    setInput("")
    fetch(`/api/auctions/${listingId}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message_type: "message", body: text }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.item || !mountedRef.current) return
        const newItem = { ...d.item, username: d.item.username ?? "?" } as AuctionChatItem
        onMessageSent({ item: newItem })
      })
      .catch(() => {})
      .finally(() => { if (mountedRef.current) setSending(false) })
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">Live Chat</span>
          <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
        </div>
      </div>
      <div
        ref={scrollRef}
        className="space-y-1.5 overflow-y-auto px-1 pt-4 max-h-48 min-h-[120px] scrollbar-none [&::-webkit-scrollbar]:hidden"
        style={{
          maskImage: "linear-gradient(to bottom, transparent 0%, black 12%, black 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 12%, black 100%)",
        }}
      >
        {chatItems.map((item) => {
          const avatarUrl = getAvatarUrl((item as { avatar_url?: string | null }).avatar_url) || undefined
          return (
            <div
              key={`${item.type}-${item.id}`}
              className={`flex items-start gap-2 py-1.5 px-2 rounded-full animate-in fade-in duration-200 ${
              item.type === "bid"
                ? "bg-white/5 border border-white/10"
                : item.type === "hype"
                  ? "bg-orange-500/5"
                  : ""
            }`}
            >
              <Avatar
              className={`w-5 h-5 shrink-0 mt-0.5 ${
                item.type === "bid" ? "bg-white text-black" : "bg-neutral-800 text-neutral-400"
              }`}
              >
                {avatarUrl && <AvatarImage src={avatarUrl} alt={item.username || ""} />}
                <AvatarFallback className={`text-[10px] font-bold ${item.type === "bid" ? "bg-white text-black" : "bg-neutral-800 text-neutral-400"}`}>
                  {(item.username || "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
              {item.type === "bid" ? (
                <p className="text-sm">
                  <span className="font-semibold text-white">{item.username}</span>
                  <span className="text-neutral-500">{" bid "}</span>
                  <span className="font-mono font-bold text-green-400">{formatPrice(String((item as { amount: number }).amount), currency ?? "USD")}</span>
                </p>
              ) : (
                <p className="text-sm">
                  <span className={`font-semibold ${item.type === "hype" ? "text-orange-400" : "text-white"}`}>
                    {item.username}
                  </span>
                  <span className="text-neutral-400">{" "}{(item as { body: string }).body}</span>
                  {item.type === "hype" && <Flame className="inline w-3 h-3 text-orange-400 ml-1" />}
                </p>
              )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-neutral-800">
        {!canWrite && currentUserId && (
          <p className="text-xs text-neutral-500">Only bidders and the seller can chat.</p>
        )}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder={canWrite ? "Say something..." : "Place a bid to join the chat"}
            className="flex-1 px-4 py-2.5 bg-neutral-900 border border-neutral-800 rounded-full text-base md:text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-neutral-600 disabled:opacity-60"
            disabled={!currentUserId || !canWrite}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!currentUserId || !canWrite || !input.trim() || sending}
            className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-neutral-200 transition-colors shrink-0 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
